import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { nativeIssueTableEntryPoint } from '@atlassian/jira-native-issue-table/src/ui/entrypoint.tsx';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type {
	JiraIssueSearchScope,
	JiraIssueSearchSettings,
	JiraIssueSearchStaticViewInput,
} from '@atlassian/jira-timeline/src/__generated__/src_timelineQuery.graphql';
import timelineParameters from '@atlassian/jira-timeline/src/__generated__/src_timelineQuery$parameters';
import businessTimelineParameters from './src/__generated__/src_jiraHarmonisedBusinessTimelineQuery$parameters';

const store =
	typeof window !== 'undefined' ? createLocalStorageProvider('jwm-view-preferences') : undefined;

const and = (clauses: string[]): string => {
	return clauses
		.map((clause) => clause.trim())
		.filter((clause) => clause.length > 0)
		.join(' AND ');
};

const getProjectJql = (projectKey: string) =>
	projectKey.length > 0 ? `project = '${projectKey}'` : '';

const getSearchTextJql = (text?: string) => {
	if (text === undefined || text.trim() === '') {
		return '';
	}
	const sanitize = (value: string) =>
		value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\t/g, ' ');

	const issueKeyRegex = new RegExp('^([A-Z][A-Z0-9_]+-\\d+)$');
	const japaneseAndChineseRegex =
		/[\p{Script_Extensions=Hiragana}\p{Script_Extensions=Katakana}\p{Script_Extensions=Han}]/u;

	let search = sanitize(text);

	if (issueKeyRegex.test(text)) {
		return `(summary ~ '${search}*' OR description ~ '${search}*' OR key = '${search}')`;
	}

	// if the text does not contain Chinese or Japanese characters (which are not tokenized),
	// and does not end with a space character
	// make it a wildcard search
	if (!japaneseAndChineseRegex.test(text) && !text.endsWith(' ')) {
		search = `${search}*`;
	}

	return `(summary ~ '${search}' OR description ~ '${search}')`;
};

const buildJql = (filter = '', text = '', projectKey = '') => {
	const search = getSearchTextJql(text);
	const project = getProjectJql(projectKey);
	return `${and([search, filter, project])} ORDER BY rank ASC`;
};

// should be equivalent to the getToday function in jira/src/packages/software/roadmap/utils/src/utils/dates.tsx
const getToday = (): number => {
	const now = new Date();
	const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
	return date.valueOf();
};

const parseQueryParamValue = (str?: string): boolean | undefined => {
	try {
		return str ? JSON.parse(str) : undefined;
	} catch (e) {
		// Do nothing - ignore invalid values
	}
};

const toStaticViewInput = (settings: JiraIssueSearchSettings): JiraIssueSearchStaticViewInput => {
	return {
		isGroupingEnabled: !!settings.groupBy,
		isHideDoneEnabled: settings.hideDone,
		isHierarchyEnabled: settings.hierarchyEnabled,
	};
};

const getFilterPreference = (entityId?: string, preferenceId = 'filter'): string => {
	if (entityId === undefined) {
		return '';
	}

	const key = `${entityId}_timeline_${preferenceId}`;
	return store?.get(key);
};

const getHideDonePreference = (entityId?: string, preferenceId = 'hideDone'): true | undefined => {
	if (entityId === undefined) {
		return undefined;
	}

	const key = `${entityId}_timeline_${preferenceId}`;
	const value = store?.get(key);

	// We only save the value in local storage if it's true, so we can ignore
	// anything else
	return value === 'true' || undefined;
};

const getProjectId = (projectKey: string): string => {
	const key = 'timeline_project-mapping-preference';
	const projectMapping: Record<string, number> = store?.get(key) ?? {};
	const projectId: number | undefined = projectMapping[projectKey];
	return projectId?.toString() ?? '';
};

export const timelineEntrypoint = createEntryPoint({
	root: JSResourceForUserVisible(
		() => import(/* webpackChunkName: "async-harmonised-business-timeline" */ './src'),
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		const {
			context,
			tenantContext: { cloudId },
		} = entryPointParams;
		const namespace = 'TIMELINE';
		const projectKey = context.match.params.projectKey;
		const { filter: queryFilter, hideDone, text } = context.query;

		if (projectKey == null) {
			throw new Error('Project key missing');
		}

		const projectId = getProjectId(projectKey);

		const storageFilter = getFilterPreference(projectId);
		const filter = queryFilter || storageFilter;

		const storageHideDone = fg('jira_timeline_server-side_hide_done_preference')
			? undefined
			: getHideDonePreference(projectId);

		const prevFieldSetIds = ['issuetype', 'issuekey', 'summary', 'timeline'];
		// TODO hardcoded fieldsetIds will be replaced with fieldSets from BE
		const newFieldSetIds = ['issuetype', 'issuekey', 'summary', 'assignee', 'status', 'timeline'];

		const fieldSetIds = fg('jira_timeline_sticky_assignee_status_columns')
			? newFieldSetIds
			: prevFieldSetIds;

		const scope: JiraIssueSearchScope | undefined = fg(
			'jira_timeline_sticky_assignee_status_columns',
		)
			? {
					operationScope: 'TIMELINE_PROJECT',
					projectKey,
				}
			: undefined;

		const jql = buildJql(filter, text, projectKey);

		const settings: JiraIssueSearchSettings = {
			groupBy: null,
			hideDone: parseQueryParamValue(hideDone) ?? storageHideDone,
			hierarchyEnabled: true,
			jql,
		};

		// JiraIssueSearchSettings will replace JiraIssueSearchStaticViewInput
		// as part of the saved views project, so we'll use it as the source of
		// truth
		const staticViewInput: JiraIssueSearchStaticViewInput = toStaticViewInput(settings);

		return {
			extraProps: {
				today: getToday(),
				isTimelineHarmonisationM2Enabled: expValEquals(
					'timeline_harmonisation_m2',
					'isEnabled',
					true,
					false,
				),
			},
			queries: {
				businessTimelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: businessTimelineParameters,
					variables: {
						cloudId,
						projectKey,
					},
				},
				timelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: timelineParameters,
					variables: {
						amountOfColumns: 500,
						cloudId,
						fieldSetIds,
						fieldSetsInput: { fieldSetIds },
						filterId: null,
						first: 50,
						issueSearchInput: { jql },
						namespace,
						projectKey,
						settings,
						shouldQueryFieldSetsById: false,
						shouldQueryHasChildren: true,
						scope,
						staticViewInput,
						viewConfigInput: { staticViewInput },
						viewQueryInput: {
							projectKeyAndItemIdQuery: {
								cloudId,
								projectKey,
								itemId: 'timeline',
							},
						},
						viewId: fg('jira_timeline_graphql_view_type') ? 'timeline_default' : 'list_default',
						isInlineEditingEnabled: fg('jira_timeline_sticky_assignee_status_columns'),
					},
				},
			},
			...(fg('jira_harmonised_timeline_prefetch_issuetable')
				? {
						entryPoints: {
							nativeIssueTableEntryPoint: {
								entryPoint: nativeIssueTableEntryPoint,
								entryPointParams,
							},
						},
					}
				: {}),
		};
	},
});
